import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { API } from '../../utils/api';
import { GuidanceCard } from '../../components/GuidanceCard';
import axios from 'axios';
import { 
  CheckCircle, 
  Circle, 
  Clock, 
  ArrowRight,
  ArrowUp,
  FileText,
  Question,
  CaretRight,
  Lock,
  Star,
  Sparkle,
  IdentificationCard,
  Bank,
  Phone,
  House,
  Car,
  FirstAid,
  Briefcase,
  Notebook
} from '@phosphor-icons/react';
import { ScrollToTop } from '../../components/ScrollToTop';

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.06
    }
  }
};

export default function DashboardPage() {
  const { language, t } = useLanguage();
  const { user, isAuthenticated, isPaid } = useAuth();
  const navigate = useNavigate();
  
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const response = await axios.get(`${API}/checklist`);
        setChecklist(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          navigate('/onboarding');
        } else {
          setError('Failed to load checklist');
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchChecklist();
    }
  }, [isAuthenticated, navigate]);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
            <p className="text-[#6B7280]">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="pt-32 max-w-[420px] mx-auto px-4">
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate progress (exclude optional items)
  const coreItems = checklist?.items?.filter(i => !i.is_optional) || [];
  const totalItems = coreItems.length;
  const completedItems = coreItems.filter(i => i.status === 'completed').length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Group items by section
  const sections = {
    day_1_3: checklist?.items?.filter(i => i.section === 'day_1_3') || [],
    day_4_7: checklist?.items?.filter(i => i.section === 'day_4_7') || [],
    day_8_14: checklist?.items?.filter(i => i.section === 'day_8_14') || [],
    day_15_30: checklist?.items?.filter(i => i.section === 'day_15_30') || [],
    after_arrival: checklist?.items?.filter(i => i.section === 'after_arrival') || []
  };

  // Get next incomplete item
  const nextItem = checklist?.items?.find(i => i.status !== 'completed' && (!i.is_optional || i.section === 'day_1_3'));

  // Section labels
  const sectionLabels = {
    day_1_3: language === 'fr' ? 'COMMENCEZ ICI' : 'START HERE',
    day_4_7: language === 'fr' ? 'PROCHAINES DÉMARCHES' : 'NEXT STEPS',
    day_8_14: language === 'fr' ? 'À PRÉPARER' : 'PREPARE NEXT',
    day_15_30: language === 'fr' ? 'LORSQUE LES CONDITIONS SONT REMPLIES' : 'WHEN REQUIREMENTS ARE MET',
    after_arrival: language === 'fr' ? 'SELON VOTRE SITUATION' : 'DEPENDING ON YOUR SITUATION'
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      <ScrollToTop />
      
      <main className="pt-24 pb-24">
        {/* Responsive container: Mobile-first with 420px max, wider on desktop */}
        <div className="max-w-[420px] lg:max-w-4xl mx-auto px-4 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
          
          {/* Welcome Header */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <h1 
              className="text-[24px] font-semibold text-[#111827] leading-tight"
              style={{ marginBottom: '4px' }}
              data-testid="dashboard-welcome"
            >
              {t('dashboard.welcome')}, {user?.first_name || 'there'}
            </h1>
            <p 
              className="text-[14px] text-[#6B7280]"
              style={{ marginBottom: '16px' }}
            >
              {t('dashboard.greeting')}
            </p>
          </motion.div>

          {/* Free user upgrade banner */}
          {!isPaid && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mb-6 p-4 rounded-[16px] bg-gradient-to-br from-[#111827]/[0.03] to-[#111827]/[0.06] border border-[#111827]/10"
              data-testid="upgrade-banner"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-[10px] bg-[#111827]/5 flex items-center justify-center flex-shrink-0">
                  <Sparkle size={20} className="text-[#111827]" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#111827] text-[14px] mb-0.5">{t('premium.upgradeTitle')}</p>
                  <p className="text-[12px] text-[#6B7280] leading-snug">{t('premium.upgradeDescription')}</p>
                </div>
              </div>
              <Link
                to="/pricing"
                className="mt-3 w-full h-10 bg-[#111827] hover:bg-[#1f2937] text-white font-medium rounded-[10px] transition-colors flex items-center justify-center gap-2 text-[14px]"
                data-testid="upgrade-cta"
              >
                {t('premium.upgradeCta')}
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          )}

          {/* Progress Card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-[16px] border border-[#F1F5F9] p-4 mb-6"
            data-testid="progress-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-[16px] font-semibold text-[#111827]">{t('dashboard.yourChecklist')}</h2>
                <p className="text-[13px] text-[#6B7280]">
                  {isPaid 
                    ? `${completedItems} / ${totalItems} ${language === 'fr' ? 'terminées' : 'completed'}`
                    : `${totalItems} ${language === 'fr' ? 'étapes' : 'steps'}`
                  }
                </p>
              </div>
              {isPaid && (
                <span className="text-[20px] font-semibold text-[#111827]">
                  {progressPercent}%
                </span>
              )}
            </div>
            
            {isPaid ? (
              <div role="progressbar" aria-label={t('dashboard.yourChecklist')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPercent} className="h-[6px] rounded-full bg-[#E5E7EB] overflow-hidden" data-testid="dashboard-core-progress">
                <div 
                  className="h-full bg-[#111827] rounded-full transition-all duration-500 ease-out" 
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 rounded-[10px] bg-[#F8FAFC] border border-[#E5E7EB]">
                <Lock size={14} className="text-[#9CA3AF]" />
                <span className="text-[12px] text-[#6B7280]">
                  {language === 'fr' ? 'Progression disponible avec Premium' : 'Progress available with Premium'}
                </span>
              </div>
            )}
          </motion.div>

          <p className="text-sm text-slate-600 mb-2" data-testid="checklist-eligibility-note">{language === 'fr' ? 'Vos réponses orientent les priorités ; elles ne confirment pas votre admissibilité.' : 'Your answers guide priorities; they do not confirm eligibility.'}</p>
          <p className="text-sm text-slate-600 mb-5" data-testid="checklist-order-note">{language === 'fr' ? 'Un ordre suggéré, pas des échéances fixes. Vérifiez les conditions de chaque étape.' : 'A suggested order, not fixed deadlines. Check the requirements for each step.'}</p>
          {sections.after_arrival.length > 0 && <a href="#situation-steps" className="block mb-5 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 underline underline-offset-4 focus-ring" data-testid="situation-steps-jump-link">{language === 'fr' ? 'Logement, transport et autres besoins : voir les étapes selon votre situation' : 'Housing, transport and other needs: see steps for your situation'}</a>}
          {/* Checklist Sections */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="space-y-6" 
            data-testid="checklist-sections"
          >
            {Object.entries(sections).map(([sectionKey, items]) => (
              items.length > 0 && (
                <motion.div
                  key={sectionKey}
                  id={sectionKey === 'after_arrival' ? 'situation-steps' : undefined}
                  tabIndex={sectionKey === 'after_arrival' ? -1 : undefined}
                  className="scroll-mt-24"
                  variants={fadeIn}
                >
                  {/* Section Title */}
                  {sectionKey === 'after_arrival' ? (
                    <div 
                      className="border border-[#E5E7EB] rounded-[12px] bg-white p-3 mb-3"
                    >
                      <div className="flex items-center gap-2">
                        <span 
                          className="text-[12px] font-semibold uppercase text-[#475569]"
                          style={{ letterSpacing: '0.08em' }}
                        >
                          {sectionLabels[sectionKey]}
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#F3F4F6] text-[#475569]" data-testid="situation-steps-label">
                          {language === 'fr' ? 'Selon vos besoins' : 'As needed'}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-600" data-testid="situation-steps-progress-note">{language === 'fr' ? 'Ces étapes peuvent être prioritaires pour vous. Elles ne sont pas incluses dans le pourcentage de progression.' : 'These steps may be priorities for you. They are not included in the progress percentage.'}</p>
                    </div>
                  ) : (
                    <h3 
                      className="text-[12px] font-semibold uppercase text-[#475569] mb-2"
                      style={{ letterSpacing: '0.08em', marginTop: '16px', marginBottom: '8px' }}
                    >
                      {sectionLabels[sectionKey]}
                    </h3>
                  )}
                  
                  {/* Step Cards */}
                  <div className="space-y-2">
                    {items.map((item, index) => {
                      const isNextStep = nextItem && item.item_id === nextItem.item_id;
                      return (
                        <motion.div
                          key={item.item_id}
                          variants={fadeIn}
                        >
                          <StepCard 
                            item={item} 
                            language={language}
                            t={t}
                            isPaid={isPaid}
                            isNextStep={isNextStep}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )
            ))}
          </motion.div>

          {/* Bottom spacing for floating button */}
          <div className="h-20 lg:h-8" />
          </div>

          {/* Desktop Sidebar - In grid column on larger screens */}
          <div className="hidden lg:block space-y-4">
            {/* User Info */}
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-[0_4px_20px_rgba(0,0,0,0.04)] p-5">
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.first_name}
                    className="w-12 h-12 rounded-[10px] object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-[10px] bg-[#F1F5F9] flex items-center justify-center">
                    <span className="font-bold text-lg text-[#111827]">
                      {user?.first_name?.[0] || 'U'}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-[#111827]">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-sm text-[#6B7280] capitalize">
                    {user?.province?.replace('_', ' ') || 'Ontario'}
                  </p>
                </div>
                {isPaid && (
                  <span className="ml-auto px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium">
                    <Star size={10} weight="fill" className="inline mr-1" />
                    Premium
                  </span>
                )}
              </div>
            </div>

            {/* Document Review Card */}
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-[0_4px_20px_rgba(0,0,0,0.04)] p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-[8px] bg-[#F1F5F9] flex items-center justify-center">
                  <FileText size={18} className="text-[#111827]" weight="duotone" />
                </div>
                <h3 className="font-semibold text-[#111827] text-[15px]">{t('dashboard.documents.title')}</h3>
              </div>
              
              {isPaid ? (
                <>
                  <p className="text-sm text-[#6B7280] mb-4 leading-relaxed">
                    {t('dashboard.documents.description')}
                  </p>
                  <Link 
                    to="/documents" 
                    className="w-full bg-[#F8FAFC] hover:bg-[#F1F5F9] text-[#111827] font-medium rounded-[8px] px-4 py-2.5 transition-colors inline-flex justify-center items-center text-sm border border-[#E5E7EB]"
                  >
                    {t('dashboard.documents.upload')}
                  </Link>
                </>
              ) : (
                <Link 
                  to="/pricing" 
                  className="w-full bg-[#111827] hover:bg-[#1f2937] text-white font-medium rounded-[8px] px-4 py-2.5 transition-colors inline-flex justify-center items-center text-sm"
                >
                  {t('premium.lockedButton')}
                </Link>
              )}
            </div>

            {/* Guidance Card */}
            <GuidanceCard contextType="dashboard" />

            {/* Disclaimer */}
            <div className="p-4 rounded-[12px] bg-[#F8FAFC] border border-[#E5E7EB]">
              <p className="text-xs text-[#9CA3AF] leading-relaxed text-center">
                {language === 'fr' 
                  ? 'PaperPal fournit des conseils, pas des avis juridiques.' 
                  : 'PaperPal provides guidance, not legal advice.'}
              </p>
            </div>
          </div>
          </div>
        </div>
      </main>

      {/* Floating Scroll to Top Button */}
      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-[#111827] text-white flex items-center justify-center z-50"
          style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.15)' }}
          data-testid="scroll-to-top-btn"
        >
          <ArrowUp size={22} weight="bold" />
        </motion.button>
      )}
    </div>
  );
}

// Get icon based on item title/type
function getStepIcon(title) {
  const t = title?.toLowerCase() || '';
  if (t.includes('sin') || t.includes('nas')) return IdentificationCard;
  if (t.includes('bank') || t.includes('banque')) return Bank;
  if (t.includes('phone') || t.includes('téléphone') || t.includes('mobile')) return Phone;
  if (t.includes('hous') || t.includes('rent') || t.includes('logement') || t.includes('loyer')) return House;
  if (t.includes('transport') || t.includes('presto') || t.includes('opus') || t.includes('compass')) return Car;
  if (t.includes('health') || t.includes('insurance') || t.includes('santé') || t.includes('assurance')) return FirstAid;
  if (t.includes('job') || t.includes('work') || t.includes('employ') || t.includes('travail') || t.includes('formulaire')) return Briefcase;
  return Notebook;
}

function StepCard({ item, language, t, isPaid, isNextStep = false }) {
  const isOptional = item.is_optional === true;
  const title = language === 'fr' ? item.title_fr : item.title_en;
  const Icon = getStepIcon(title);

  // Status badge colors
  const getStatusBadge = () => {
    if (isOptional && item.status === 'not_started') {
      return (
        <span className="inline-flex items-center px-[10px] py-[6px] rounded-full text-[12px] font-medium bg-[#F3F4F6] text-[#6B7280]">
          {language === 'fr' ? 'Selon vos besoins' : 'As needed'}
        </span>
      );
    }
    
    switch (item.status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-[10px] py-[6px] rounded-full text-[12px] font-medium bg-[#DCFCE7] text-[#166534]">
            {t('dashboard.status.completed')}
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center px-[10px] py-[6px] rounded-full text-[12px] font-medium bg-[#FEF3C7] text-[#92400E]">
            {t('dashboard.status.in_progress')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-[10px] py-[6px] rounded-full text-[12px] font-medium bg-[#F3F4F6] text-[#6B7280]">
            {t('dashboard.status.not_started')}
          </span>
        );
    }
  };

  // Card styles based on state
  const cardClasses = isNextStep
    ? 'bg-[#F1F7F5] border border-[#80B9AD] rounded-[14px] p-4 transition-all duration-150 hover:bg-[#F3F4F6] active:bg-[#F3F4F6]'
    : isOptional && item.status !== 'completed'
      ? 'bg-white border border-dashed border-[#E5E7EB] rounded-[14px] p-[14px] transition-all duration-150 hover:bg-[#F9FAFB] active:bg-[#F9FAFB]'
      : 'bg-white border border-[#F1F5F9] rounded-[14px] p-[14px] transition-all duration-150 hover:bg-[#F9FAFB] active:bg-[#F9FAFB]';

  const shadowStyle = isNextStep 
    ? { boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }
    : {};

  return (
    <Link
      to={`/checklist/${item.item_id}`}
      className={`block ${cardClasses}`}
      style={shadowStyle}
      data-testid={`checklist-item-${item.item_id}`}
    >
      {/* Recommended label for next step */}
      {isNextStep && (
        <p className="text-[11px] text-[#236C64] mb-2 font-semibold" data-testid={`checklist-next-${item.item_id}`}>
          {language === 'fr' ? 'À SUIVRE DANS VOTRE PLAN' : 'NEXT IN YOUR PLAN'}
        </p>
      )}
      
      <div className="flex items-start gap-3">
        {/* Left Icon Circle */}
        <div className="w-8 h-8 rounded-full bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
          {item.status === 'completed' ? (
            <CheckCircle size={16} className="text-[#111827]" weight="fill" />
          ) : item.status === 'in_progress' ? (
            <Clock size={16} className="text-[#F59E0B]" weight="fill" />
          ) : (
            <Icon size={16} className="text-[#6B7280]" weight="duotone" />
          )}
        </div>
        
        {/* Title */}
        <div className="flex-1 min-w-0">
          <h4 
            className={`text-[15px] font-medium leading-[1.3] [overflow-wrap:anywhere] ${
              item.status === 'completed' ? 'text-[#64748B] line-through' : 'text-[#111827]'
            }`}
          >
            {title}
          </h4>
          {item[`priority_reason_${language}`] && <p className="mt-2 text-xs leading-relaxed text-slate-600 [overflow-wrap:anywhere]" data-testid={`checklist-priority-reason-${item.item_id}`}>{item[`priority_reason_${language}`]}</p>}
        </div>
        
        {/* Status Badge + Arrow */}
        <div className="flex items-center gap-2 flex-shrink-0 max-w-[38%]">
          {getStatusBadge()}
          <CaretRight size={16} className="text-[#9CA3AF]" />
        </div>
      </div>
    </Link>
  );
}

